/**
 * Enhanced Control Implementation Modal with GitHub Evidence Collection
 * Modal for marking controls as implemented with both traditional evidence and GitHub evidence
 */

import { useState, useRef, useEffect } from 'react';
import FormModal from '../../../components/ui/FormModal/FormModal';
import { Button, buttonVariants } from '../../../components/ui/Button2';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/Tabs2';
import { FieldLabel, FieldError } from '../../../components/ui/Field';
import { Icon } from '../../../components/ui/Icon';
import CategoryPill from '../../../components/ui/CategoryPill/CategoryPill';
import logger from '../../../utils/logger';
import enhancedStyles from './ControlImplementationModalEnhanced.module.css';
// Story 6.3: GitHub methods come from the dedicated singleton (still
// alive — only AthenaBackendService + Extensions are being deleted).
// The legacy modal contract used a shimmed `backendService` prop that
// had GitHub methods Object.assign'd onto it via extendWithGitHubEvidence;
// now we call the singleton directly and drop the prop.
import GitHubEvidenceService from '../../../services/GitHubEvidenceService';

const ControlImplementationModalEnhanced = ({
  isOpen,
  onClose,
  control,
  onSave,
  sessionId,
  tenantId
}) => {
  const isEditing = control?.implementation_status === 'implemented';

  // Declarative form schema — the three simple fields are owned by
  // FormModal (state, validation, submit lifecycle). The evidence
  // collector (file upload + GitHub) is bespoke and lives in the
  // FormModal `children` slot, managed by the local state below.
  const FIELDS = [
    { name: 'implementation_date', type: 'date', label: 'Implementation Date', required: true },
    { name: 'implemented_by', type: 'text', label: 'Implemented By', placeholder: 'John Smith' },
    {
      name: 'implementation_notes',
      type: 'textarea',
      label: 'Implementation Notes',
      required: true,
      rows: 5,
      placeholder:
        'Describe how the control was implemented, what tests were performed, and any relevant details...',
    },
  ];

  const initialValues = {
    implementation_date: control?.implementation_date
      ? new Date(control.implementation_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    implemented_by: control?.implemented_by || '',
    implementation_notes: control?.implementation_notes || '',
  };

  // Multiple file uploads state (separate so the evidence slot can
  // manage add/remove independently of the FormModal field values).
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Evidence mode state (upload or github)
  const [evidenceMode, setEvidenceMode] = useState('upload'); // 'upload' or 'github'
  const fileInputRef = useRef(null);

  // GitHub evidence state
  const [githubRepository, setGithubRepository] = useState('');
  const [githubRepositories, setGithubRepositories] = useState([]);
  const [githubConnected, setGithubConnected] = useState(false);
  const [searchDepth, setSearchDepth] = useState('standard');
  const [isCollectingEvidence, setIsCollectingEvidence] = useState(false);
  const [collectionJobId, setCollectionJobId] = useState(null);
  const [collectionStatus, setCollectionStatus] = useState(null);
  const [githubEvidence, setGithubEvidence] = useState([]);
  const [githubError, setGithubError] = useState(null);

  const [uploadError, setUploadError] = useState(null);
  const pollIntervalRef = useRef(null);

  // Load connected GitHub repositories and check for existing evidence on mount
  useEffect(() => {
    checkGitHubConnection();
    checkExistingEvidence();

    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Initialize uploadedFiles from existing control evidence
  useEffect(() => {
    if (control?.evidence) {
      // Handle array format (new format)
      if (Array.isArray(control.evidence)) {
        setUploadedFiles(control.evidence);
      }
      // Handle legacy string format (JSON string)
      else if (typeof control.evidence === 'string') {
        try {
          const parsed = JSON.parse(control.evidence);
          if (Array.isArray(parsed)) {
            setUploadedFiles(parsed);
          } else if (parsed && typeof parsed === 'object') {
            // Single object - wrap in array
            setUploadedFiles([parsed]);
          }
        } catch {
          // Not JSON - legacy format, create a placeholder
          logger.debug('Legacy evidence format detected');
        }
      }
    }
  }, [control]);

  // Check GitHub connection status and load repositories
  const checkGitHubConnection = async () => {
    try {
      // First check if GitHub is connected
      const statusResponse = await GitHubEvidenceService.getGitHubStatus();

      if (statusResponse && statusResponse.connected) {
        logger.info('GitHub is connected, loading repositories...');
        setGithubConnected(true);

        // If connected, load the repositories
        await loadGitHubRepositories();
      } else {
        logger.info('GitHub is not connected');
        setGithubConnected(false);
        setGithubRepositories([]);
      }
    } catch (error) {
      logger.error('Failed to check GitHub connection:', error);
      // If the status check fails, try loading repositories anyway
      // This handles cases where the status endpoint might not exist
      await loadGitHubRepositories();
    }
  };

  // Load connected GitHub repositories
  const loadGitHubRepositories = async () => {
    try {
      const response = await GitHubEvidenceService.getGitHubRepositories();
      if (response.repositories && response.repositories.length > 0) {
        setGithubRepositories(response.repositories);
        setGithubConnected(true); // If we have repositories, GitHub must be connected
        // Set the first repository as default
        setGithubRepository(response.repositories[0].full_name);
      } else {
        // Even if no repositories, GitHub might still be connected
        // (e.g., app installed but no repositories selected)
        logger.info('No repositories found, but GitHub may still be connected');
      }
    } catch (error) {
      logger.error('Failed to load GitHub repositories:', error);
      setGithubRepositories([]);
    }
  };

  // Check for existing evidence when modal opens
  const checkExistingEvidence = async () => {
    if (!sessionId || !control?.control_id) {
      return;
    }

    try {
      logger.debug('Checking for existing evidence...');
      const response = await GitHubEvidenceService.getSessionEvidence(sessionId, control.control_id);

      if (response.evidence && response.evidence.length > 0) {
        logger.info(`Found ${response.evidence.length} existing evidence items`);
        setGithubEvidence(response.evidence);
        setCollectionStatus(`Found ${response.evidence.length} existing evidence items`);

        // Extract repository from first evidence if available
        if (response.evidence[0]?.repository_full_name) {
          setGithubRepository(response.evidence[0].repository_full_name);
        }
      } else {
        logger.debug('No existing evidence found');
      }
    } catch (error) {
      logger.debug('No existing evidence or error checking:', error);
      // Not an error - just means no evidence exists yet
    }
  };

  // Handle file upload (supports multiple files)
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/markdown',
      'text/x-markdown',
      'image/jpeg',
      'image/png'
    ];

    // Allowed extensions (for browsers that don't set correct MIME type)
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.jpg', '.jpeg', '.png'];

    const validFiles = [];
    for (const file of files) {
      // Validate file size (max 10MB per file)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError(`File "${file.name}" exceeds 10MB limit`);
        continue;
      }

      // Validate file type (check MIME type or extension)
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);

      if (!isValidType) {
        setUploadError(`File "${file.name}" is not a supported format`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...validFiles]);
      setUploadError(null);
      logger.info(`${validFiles.length} evidence file(s) selected`);
    }

    // Reset file input to allow re-selecting same files
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove specific uploaded file by index
  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Collect GitHub evidence
  const collectGitHubEvidence = async () => {
    if (!githubRepository) {
      setGithubError('Please select a GitHub repository');
      return;
    }

    setIsCollectingEvidence(true);
    setGithubError(null);
    setCollectionStatus('Starting evidence collection...');

    try {
      // Build requirements data for evidence collection
      const requirementData = control.requirements?.map(req => ({
        req_id: req.req_id,
        req_statement: req.req_statement,
        req_rationale: req.req_rationale
      })) || [];

      // Call the evidence collection endpoint
      const response = await GitHubEvidenceService.collectGitHubEvidence({
        session_id: sessionId,
        control_id: control.control_id,
        control_name: control.control_name,
        control_description: control.control_description,
        control_type: control.control_type || 'Security',
        requirements: requirementData,
        repository_full_name: githubRepository,
        search_depth: searchDepth
      });

      // Backend returns job_ids (array) for batch collection
      if (response.job_ids && response.job_ids.length > 0) {
        // Use the first job ID for now (or could track all)
        const jobId = response.job_ids[0];
        setCollectionJobId(jobId);
        setCollectionStatus(`Started ${response.total_requirements} evidence collection job(s)...`);
        // Start polling for job status
        pollEvidenceCollectionStatus(jobId);
      } else if (response.job_id) {
        // Fallback for single job response
        setCollectionJobId(response.job_id);
        pollEvidenceCollectionStatus(response.job_id);
      } else {
        throw new Error('Failed to start evidence collection - no job ID returned');
      }
    } catch (error) {
      logger.error('GitHub evidence collection failed:', error);
      setGithubError(error.message || 'Failed to collect evidence from GitHub');
      setIsCollectingEvidence(false);
    }
  };

  // Poll for evidence collection status
  const pollEvidenceCollectionStatus = (jobId) => {
    let pollCount = 0;
    const maxPolls = 30; // Max 30 polls (1 minute with 2-second intervals)

    // Check status immediately
    const checkStatus = async () => {
      try {
        const status = await GitHubEvidenceService.getEvidenceCollectionStatus(sessionId);

        logger.debug('Evidence collection status:', status);
        setCollectionStatus(`Collection ${status.collection_status}: ${status.total_evidence} evidence items found`);

        if (status.collection_status === 'completed' || status.total_evidence > 0) {
          // Collection complete - fetch the evidence
          clearInterval(pollIntervalRef.current);
          await fetchCollectedEvidence();
          setIsCollectingEvidence(false);
          setCollectionStatus(`Collection complete: ${status.total_evidence} evidence items found`);
          return true;
        } else if (status.collection_status === 'failed' && status.failed_jobs > 0) {
          clearInterval(pollIntervalRef.current);
          setGithubError('Evidence collection failed. Please try again.');
          setIsCollectingEvidence(false);
          return true;
        }

        pollCount++;
        if (pollCount >= maxPolls) {
          // Even if still processing, try to fetch whatever evidence was collected
          clearInterval(pollIntervalRef.current);
          await fetchCollectedEvidence();
          setIsCollectingEvidence(false);

          if (status.total_evidence > 0) {
            setCollectionStatus(`Found ${status.total_evidence} evidence items`);
          } else {
            setGithubError('No evidence found. Try adjusting search parameters.');
          }
          return true;
        }

        return false;
      } catch (error) {
        logger.error('Failed to poll evidence status:', error);
        // Try to fetch evidence anyway
        await fetchCollectedEvidence();
        clearInterval(pollIntervalRef.current);
        setIsCollectingEvidence(false);
        return true;
      }
    };

    // Check immediately
    checkStatus();

    // Then poll periodically
    pollIntervalRef.current = setInterval(checkStatus, 2000);
  };

  // Fetch collected evidence
  const fetchCollectedEvidence = async () => {
    try {
      logger.debug('Fetching evidence for session:', sessionId, 'control:', control.control_id);
      const response = await GitHubEvidenceService.getSessionEvidence(sessionId, control.control_id);

      logger.debug('Evidence response:', response);

      if (response.evidence && response.evidence.length > 0) {
        setGithubEvidence(response.evidence);
        logger.info(`Found ${response.evidence.length} evidence items`);
      } else {
        // No evidence found - this is not necessarily an error
        logger.info('No evidence found for the requirements');
        setGithubEvidence([]);
        setCollectionStatus('No evidence found matching the requirements');
      }
    } catch (error) {
      logger.error('Failed to fetch evidence:', error);
      // Don't show error, just set empty evidence
      setGithubEvidence([]);
      setCollectionStatus('Unable to retrieve evidence');
    }
  };


  // Handle save. Receives the three declarative field values from
  // FormModal; merges in the slot-managed evidence (uploaded files +
  // GitHub) and base64-encodes uploads before calling onSave. Throwing
  // here surfaces the message as FormModal's inline submit error and
  // keeps the modal open (FormModal only closes on a resolved onSubmit).
  const handleSubmit = async (values) => {
    // Prepare save data from FormModal's field values + derived GitHub meta.
    const saveData = {
      implementation_date: values.implementation_date,
      implemented_by: values.implemented_by,
      implementation_notes: values.implementation_notes,
      github_evidence_collected: githubEvidence.length > 0,
      github_evidence_count: githubEvidence.length,
      github_repository: githubRepository,
    };

    // Process all uploaded files into evidence array
    const evidenceArray = [];

    // Convert each uploaded file to base64
    for (const file of uploadedFiles) {
      if (file instanceof File) {
        try {
          const base64Content = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          evidenceArray.push({
            filename: file.name,
            file_type: file.type,
            file_size: file.size,
            content: base64Content,
            upload_date: new Date().toISOString(),
            source: 'upload'
          });
        } catch (error) {
          logger.error(`Failed to process file ${file.name}:`, error);
          throw new Error(`Failed to process file: ${file.name}`);
        }
      } else if (typeof file === 'object' && file.filename) {
        // Already processed evidence item (from existing data)
        evidenceArray.push(file);
      }
    }

    // Set evidence as array (backend now expects array)
    saveData.evidence = evidenceArray.length > 0 ? evidenceArray : null;

    // Let the rejection bubble to FormModal (it renders the inline error
    // and leaves the modal open). onSave is the parent's updateControl.
    await onSave(saveData);
  };


  if (!isOpen) return null;

  return (
    <FormModal
      title="Control Implementation Evidence"
      headerExtra={
        <span className={enhancedStyles.controlInfo}>
          <strong>{control?.control_id}</strong> — {control?.control_name}
        </span>
      }
      fields={FIELDS}
      initialValues={initialValues}
      submitLabel={isEditing ? 'Update' : 'Confirm implementation'}
      submittingLabel="Saving..."
      onSubmit={handleSubmit}
      onClose={onClose}
      closeOnBackdrop={!isCollectingEvidence}
      closeOnEscape={!isCollectingEvidence}
      maxWidth={720}
    >
        {/* Evidence Collection Section — Upload or GitHub.
            Rendered in FormModal's children slot since it can't be
            expressed as a declarative FormField. Mode switching is the
            2.0 Tabs primitive (segmented variant). */}
        <div className={enhancedStyles.evidenceSection}>
          <FieldLabel>Evidence Collection</FieldLabel>

          <Tabs value={evidenceMode} onValueChange={setEvidenceMode}>
            <TabsList>
              <TabsTrigger value="upload">
                <Icon name="upload" size={16} />
                Upload Documents
              </TabsTrigger>
              <TabsTrigger
                value="github"
                disabled={!githubConnected}
                title={!githubConnected ? 'GitHub is not connected. Please connect GitHub first.' : 'Collect evidence from GitHub'}
              >
                <Icon name="github" size={16} />
                GitHub Evidence
              </TabsTrigger>
            </TabsList>

          {/* Upload Mode */}
          <TabsContent value="upload">
            <div className={enhancedStyles.uploadSection}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.txt,.md,.jpg,.jpeg,.png"
                multiple
                style={{ display: 'none' }}
              />

              {/* List of uploaded files */}
              {uploadedFiles.length > 0 && (
                <div className={enhancedStyles.uploadedFilesList}>
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className={enhancedStyles.uploadedFile}>
                      <div className={enhancedStyles.fileInfo}>
                        <Icon name="document" size={20} />
                        <span className={enhancedStyles.fileName}>
                          {file.name || file.filename || 'Evidence file'}
                        </span>
                        <span className={enhancedStyles.fileSize}>
                          {file.size ? `${(file.size / 1024).toFixed(1)} KB` :
                           file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : ''}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => removeUploadedFile(index)}
                          aria-label="Remove file"
                        >
                          <Icon name="close" size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button */}
              <button
                type="button"
                className={enhancedStyles.uploadButton}
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon name="upload" size={32} />
                <span>{uploadedFiles.length > 0 ? 'Add more files' : 'Click to upload evidence documents'}</span>
                <span className={enhancedStyles.uploadHint}>
                  PDF, Word, Text, Markdown, or Image (max 10MB each) - Multiple files supported
                </span>
              </button>
            </div>
          </TabsContent>

          {/* GitHub Mode */}
          <TabsContent value="github">
            {githubConnected && (
              <>
                {githubRepositories.length === 0 ? (
                  <div className={enhancedStyles.noRepositories}>
                    <Icon name="github" size={48} className={enhancedStyles.noRepositoriesIcon} />
                    <p className={enhancedStyles.noRepositoriesText}>
                      GitHub is connected but no repositories are available.
                    </p>
                    <p className={enhancedStyles.noRepositoriesHint}>
                      Please ensure the GitHub App has access to at least one repository.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={checkGitHubConnection}
                      startIcon={<Icon name="refresh" size={14} />}
                    >
                      Refresh Repositories
                    </Button>
                  </div>
                ) : (
                  <>
                <div className={enhancedStyles.githubSettings}>
                  <select
                    value={githubRepository}
                    onChange={(e) => setGithubRepository(e.target.value)}
                    disabled={isCollectingEvidence}
                  >
                    <option value="">Select a repository</option>
                    {githubRepositories.map(repo => (
                      <option key={repo.repo_id || repo.full_name} value={repo.full_name}>
                        {repo.full_name}
                      </option>
                    ))}
                  </select>

                  <select
                    className={enhancedStyles.searchDepth}
                    value={searchDepth}
                    onChange={(e) => setSearchDepth(e.target.value)}
                    disabled={isCollectingEvidence}
                  >
                    <option value="quick">Quick Search</option>
                    <option value="standard">Standard Search</option>
                    <option value="deep">Deep Search</option>
                  </select>
                </div>

                <Button
                  size="lg"
                  className={enhancedStyles.fullWidthCta}
                  onClick={collectGitHubEvidence}
                  disabled={!githubRepository}
                  loading={isCollectingEvidence}
                  startIcon={<Icon name="search" size={16} />}
                >
                  {isCollectingEvidence ? 'Collecting Evidence…' : 'Collect Evidence from Code'}
                </Button>

                {/* Collection Status */}
                {collectionStatus && (
                  <div className={enhancedStyles.collectionStatus}>
                    {collectionStatus}
                  </div>
                )}

                {/* GitHub Evidence Results */}
                {githubEvidence.length > 0 && (
                  <div className={enhancedStyles.githubEvidence}>
                    <div className={enhancedStyles.evidenceHeader}>
                      <h4>
                        Evidence Found ({githubEvidence.length} items)
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={collectGitHubEvidence}
                        disabled={isCollectingEvidence}
                        loading={isCollectingEvidence}
                        title="Re-collect evidence from GitHub"
                        startIcon={<Icon name="refresh" size={14} />}
                      >
                        {isCollectingEvidence ? 'Refreshing...' : 'Refresh'}
                      </Button>
                    </div>
                    <div className={enhancedStyles.evidenceList}>
                      {githubEvidence.map((evidence, idx) => (
                        <div key={evidence.evidence_id || idx} className={enhancedStyles.evidenceItem}>
                          <div className={enhancedStyles.evidenceHeader}>
                            <span className={enhancedStyles.requirementId}>
                              {evidence.requirement_id || 'REQ-??'}
                            </span>
                            <CategoryPill
                              tone={
                                evidence.confidence_score > 0.7 ? 'success' :
                                evidence.confidence_score > 0.5 ? 'warning' : 'error'
                              }
                              size="sm"
                              label={`${((evidence.confidence_score || 0) * 100).toFixed(0)}% confidence`}
                            />
                          </div>
                          <div className={enhancedStyles.evidenceSummary}>
                            {evidence.evidence_summary || 'Evidence found in repository'}
                          </div>

                          {/* Show evidence URL if available */}
                          {evidence.evidence_url && (
                            <div className={enhancedStyles.evidenceLocation}>
                              <Icon name="folder" size={14} />
                              <a
                                href={evidence.evidence_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={enhancedStyles.evidenceLink}
                              >
                                View on GitHub
                              </a>
                            </div>
                          )}

                          {/* Show evidence data details */}
                          {evidence.evidence_data && (
                            <>
                              {/* Show code snippets if available */}
                              {evidence.evidence_data.code_snippets && evidence.evidence_data.code_snippets.length > 0 && (
                                <div className={enhancedStyles.codeSnippets}>
                                  <strong>Code found in:</strong>
                                  {evidence.evidence_data.code_snippets.slice(0, 3).map((snippet, sidx) => (
                                    <div key={sidx} className={enhancedStyles.snippetLocation}>
                                      {snippet.file_path || snippet.path || 'Unknown file'}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Show fulfillment level */}
                              {evidence.fulfillment_contribution && (
                                <div className={enhancedStyles.fulfillmentBadge}>
                                  Fulfillment: <strong>{evidence.fulfillment_contribution}</strong>
                                </div>
                              )}
                            </>
                          )}

                          {/* Show collection timestamp */}
                          {evidence.collected_at && (
                            <div className={enhancedStyles.timestamp}>
                              <Icon name="clock" size={14} />
                              {new Date(evidence.collected_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* GitHub Error */}
                {githubError && (
                  <div className={enhancedStyles.githubError}>
                    <Icon name="warning" size={16} />
                    {githubError}
                  </div>
                )}
                  </>
                )}
              </>
            )}

            {/* No GitHub Connection Message - Only show if GitHub is NOT connected */}
            {!githubConnected && (
              <div className={enhancedStyles.noGithub}>
                <p>GitHub is not connected.</p>
                <p>Please connect GitHub first to use this feature.</p>
                <a href="/settings/integrations" className={buttonVariants()}>
                  Connect GitHub →
                </a>
              </div>
            )}
          </TabsContent>
          </Tabs>
        </div>

        {/* File-validation feedback (size / type). Save failures surface
            via FormModal's own inline submit error below this slot. */}
        {uploadError && (
          <FieldError>
            <span className={enhancedStyles.uploadErrorRow}>
              <Icon name="warning" size={16} />
              {uploadError}
            </span>
          </FieldError>
        )}
    </FormModal>
  );
};

export default ControlImplementationModalEnhanced;